import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react'
import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'

import { FeatureType } from 'clickNgo/configs/feature'
import { I2087Form } from 'clickNgo/models/crud/forms'
import { Logic, Operator } from 'core-components/types'
import { CrudStore } from 'core/types/crudStore'
import Logger from 'core/utils/Logger'

import { getQueryError } from '../lib/getQueryError'
import { mapCampaignRemindersFrom2087 } from '../lib/mapCampaignRemindersFrom2087'
import { mapCampaignReminderTo2087 } from '../lib/mapCampaignReminderTo2087'
import { CAMPAIGN_REMINDER_FORM_ID, CAMPAIGN_REMINDER_SEND_STATUS } from '../model/constants'
import { CampaignReminder } from '../model/types'

interface GetUserCampaignReminderParams
  extends Pick<CampaignReminder, 'campaignId' | 'partnerId' | 'sendTo'> {}

interface CreateUserCampaignReminderParams
  extends Omit<CampaignReminder, 'entryId' | 'sendStatus'> {}

interface UpdateCampaignReminderParams extends Omit<CampaignReminder, 'sendStatus'> {}

interface CampaignReminderTagParams
  extends Pick<CampaignReminder, 'campaignId' | 'partnerId' | 'sendTo'> {}

const campaignReminderTag = ({ campaignId, partnerId, sendTo }: CampaignReminderTagParams) =>
  `${campaignId}:${partnerId}:${sendTo}`

export const campaignReminderApi = createApi({
  reducerPath: `${FeatureType.CngApi}/campaignReminderApi`,
  baseQuery: fakeBaseQuery(),
  tagTypes: ['CampaignReminder'],
  endpoints: (build) => ({
    getUserCampaignReminder: build.query<CampaignReminder | null, GetUserCampaignReminderParams>({
      providesTags: (_result, _error, params) => [
        { type: 'CampaignReminder', id: campaignReminderTag(params) },
      ],
      queryFn: async ({ campaignId, partnerId, sendTo }) => {
        try {
          const source = CrudStore.get<I2087Form>(CrudStoreItem.CampaignReminders)

          source.setFilter({
            logic: Logic.AND,
            filters: [
              { field: 'CampaignId', operator: Operator.EQUAL, value: campaignId },
              { field: 'PartnerId', operator: Operator.EQUAL, value: partnerId },
              { field: 'SendTo', operator: Operator.EQUAL, value: sendTo },
              {
                field: 'SendStatus',
                operator: Operator.EQUAL,
                value: CAMPAIGN_REMINDER_SEND_STATUS.PENDING,
              },
            ],
          })

          const response = await source.fetch()

          if (response.items && response.items.length > 0) {
            return { data: mapCampaignRemindersFrom2087(response.items[0]) }
          }

          return { data: null }
        } catch (error) {
          Logger.error('Error fetching campaign reminders:', error)
          return { error: getQueryError(error) }
        }
      },
    }),
    createCampaignReminder: build.mutation<CampaignReminder, CreateUserCampaignReminderParams>({
      invalidatesTags: (_result, _error, params) => [
        { type: 'CampaignReminder', id: campaignReminderTag(params) },
      ],
      queryFn: async (params) => {
        try {
          const source = CrudStore.get<I2087Form>(CrudStoreItem.CampaignReminders)
          const payload = mapCampaignReminderTo2087(params)
          const response = await source.create(CAMPAIGN_REMINDER_FORM_ID, [payload])

          return {
            data: mapCampaignRemindersFrom2087((response[0] ?? payload) as I2087Form),
          }
        } catch (error) {
          Logger.error('Error creating campaign reminder:', error)
          return { error: getQueryError(error) }
        }
      },
    }),
    updateCampaignReminder: build.mutation<CampaignReminder, UpdateCampaignReminderParams>({
      invalidatesTags: (_result, _error, params) => [
        { type: 'CampaignReminder', id: campaignReminderTag(params) },
      ],
      queryFn: async (params) => {
        try {
          if (!params.entryId) {
            throw new Error('Campaign reminder entry id is required for update.')
          }

          const source = CrudStore.get<I2087Form>(CrudStoreItem.CampaignReminders)
          const payload = mapCampaignReminderTo2087(params)
          const response = await source.update(CAMPAIGN_REMINDER_FORM_ID, [payload])

          return {
            data: mapCampaignRemindersFrom2087((response[0] ?? payload) as I2087Form),
          }
        } catch (error) {
          Logger.error('Error updating campaign reminder:', error)
          return { error: getQueryError(error) }
        }
      },
    }),
    deleteCampaignReminder: build.mutation<void, CampaignReminder>({
      invalidatesTags: (_result, _error, params) => [
        { type: 'CampaignReminder', id: campaignReminderTag(params) },
      ],
      queryFn: async ({ entryId }) => {
        try {
          const source = CrudStore.get<I2087Form>(CrudStoreItem.CampaignReminders)

          await source.delete(CAMPAIGN_REMINDER_FORM_ID, { EntryId: entryId })

          return { data: undefined }
        } catch (error) {
          Logger.error('Error deleting campaign reminder:', error)
          return { error: getQueryError(error) }
        }
      },
    }),
  }),
})

export const {
  useCreateCampaignReminderMutation,
  useDeleteCampaignReminderMutation,
  useGetUserCampaignReminderQuery,
  useUpdateCampaignReminderMutation,
} = campaignReminderApi
